import { SourceGlyph, WidgetThumbnail, cx } from '@flexbyt/widget-preview';
import { Heading, Text } from '../ui/type.js';

/**
 * The three steps, at a glance.
 *
 * ===========================================================================
 * A summary, not a preview
 * ===========================================================================
 * Three sentences and three small drawings. The detailed demonstrations come
 * immediately after this, so anything more here is the page saying the same
 * thing twice — and the version that says it twice is always the one where the
 * reader stops trusting that a new section means a new idea.
 *
 * The cues are deliberately abstract and deliberately *different from each
 * other*: logos converging, rows being ticked, one page with something on it.
 * Somebody scanning should be able to tell the three steps apart without
 * reading, which is the entire value a visual adds at this size.
 *
 * ===========================================================================
 * These are cards now, and that reverses an earlier decision
 * ===========================================================================
 * The note that used to sit here said "no borders around the steps — a row of
 * three bordered cards is the shape this section takes in every generated SaaS
 * page". The instinct was right and the conclusion was wrong, and it is worth
 * being precise about which part.
 *
 * What makes that shape look generated is three *identical* boxes holding three
 * interchangeable icons and three paragraphs of the same length — a card used as
 * a container because the content had no shape of its own. Set loose on the page
 * with nothing but whitespace between them, though, these three stopped reading
 * as a sequence at all: on a wide screen they were three unrelated columns of
 * grey text, and the numbers doing all the work of saying they were ordered.
 *
 * A card here is doing something a card is actually for — it draws the boundary
 * of one step, so three steps read as three things rather than as one paragraph
 * in three pieces. What keeps it from the template look is the inside: the cue
 * area is a tinted panel that differs per step, the numeral is set large in the
 * accent rather than shrunk into a circle, and the drawings are unlike each
 * other.
 */

const STEPS = [
  {
    n: '01',
    title: 'Import',
    body: 'Connect the platforms your reviews are already on. ReviewsJet brings them in and keeps them up to date.',
    cue: <ImportCue />,
  },
  {
    n: '02',
    title: 'Manage',
    body: 'Everything lands in one library. Tag it, read it, and choose which reviews are allowed on your site.',
    cue: <ManageCue />,
  },
  {
    n: '03',
    title: 'Display',
    body: 'Put the ones you chose on your website — a line of code, or the app if you are on Wix or Webflow.',
    cue: <DisplayCue />,
  },
];

export function HowItWorks() {
  return (
    <div>
      <div className="max-w-text">
        <p className="eyebrow text-brand">How it works</p>
        <Heading level={2} className="mt-4">
          From scattered reviews to your website in three steps.
        </Heading>
        <Text className="mt-4 max-w-prose">
          No forms to send, no chasing customers for feedback. The reviews
          already exist — this is the path they take to your own site.
        </Text>
      </div>

      <ol className="relative mt-14 grid gap-4 md:grid-cols-3 lg:gap-5">
        {/*
          The path the three steps sit on: a dashed rule through the middle of
          the drawings, behind the cards, visible in the gaps between them.
        */}
        <li
          aria-hidden
          className="pointer-events-none absolute inset-x-[16%] top-[5.5rem] hidden border-t-2 border-dashed border-brand/25 md:block"
        />
        {STEPS.map((step) => (
          <li
            key={step.n}
            /*
              A resting `shadow-xs` and no hover state. These are not targets —
              nothing here is clickable — and a card that lifts under the cursor
              promises a click that never comes. The lift it does have is only
              enough to separate three white cards from a white page.
            */
            className="relative flex flex-col overflow-hidden rounded-[1.75rem] bg-white p-2 shadow-[0_24px_48px_-32px_rgba(20,40,120,0.35)] ring-1 ring-black/[0.06]"
          >
            {/*
              The cue sits in a recessed panel rather than floating above the
              text. It gives each card a consistent top edge whatever the drawing
              inside is doing, which is what stops three different sketches from
              making three different-looking cards.
            */}
            <div className="relative isolate grid h-44 place-items-center overflow-hidden rounded-[1.35rem] bg-surface">
              <div
                aria-hidden
                className="grid-fade pointer-events-none absolute inset-0 -z-10 [background-size:24px_24px] [mask-image:radial-gradient(ellipse_70%_70%_at_50%_50%,black_20%,transparent_75%)]"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-1/2 -z-10 size-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/10 blur-2xl"
              />
              {step.cue}
            </div>

            <div className="flex flex-1 flex-col px-5 pt-6 pb-5">
              <p className="flex items-center gap-3">
                <span className="nums grid h-7 min-w-9 place-items-center rounded-full bg-brand-wash px-2 text-caption font-bold text-brand">
                  {step.n}
                </span>
                <span className="text-display-sm leading-none text-ink-950">
                  {step.title}
                </span>
              </p>
              <p className="mt-3.5 text-body-sm leading-relaxed text-ink-600">{step.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Five marks converging on one point. */
function ImportCue() {
  const sources = ['google', 'trustpilot', 'yelp'] as const;

  return (
    <div className="flex items-center" aria-hidden>
      <div className="flex flex-col gap-2">
        {sources.map((source) => (
          <span
            key={source}
            className="grid size-10 place-items-center rounded-xl bg-white shadow-[0_4px_12px_-4px_rgba(10,10,11,0.12)] ring-1 ring-black/[0.05]"
          >
            <SourceGlyph source={source} className="size-4.5" />
          </span>
        ))}
      </div>

      <span className="mx-3 h-px w-10 border-t-2 border-dashed border-brand/40" />
      {/* The point everything converges on is ReviewsJet, so it is the one
          coloured mark in this cue rather than another grey dot. */}
      <span className="grid size-10 place-items-center rounded-xl bg-ink-950 shadow-[0_8px_20px_-6px_rgba(31,91,240,0.6)]"><span className="size-2.5 rounded-full bg-brand ring-4 ring-brand/30" /></span>
    </div>
  );
}

/** Rows, two of them chosen. */
function ManageCue() {
  return (
    <div className="flex w-48 flex-col gap-3 rounded-2xl bg-white p-4 shadow-[0_12px_28px_-12px_rgba(10,10,11,0.25)] ring-1 ring-black/[0.05]" aria-hidden>
      {[true, false, true].map((chosen, index) => (
        <div key={index} className="flex items-center gap-2.5">
          {/* Accent for a chosen row, matching the real library below, where
              selection is `accent-50`. Choosing is the whole verb of this step,
              so it should be the thing carrying colour. */}
          <span
            className={cx(
              'grid size-4.5 shrink-0 place-items-center rounded-[5px]',
              chosen ? 'bg-brand' : 'bg-white ring-1 ring-ink-200'
            )}
          >
            {chosen ? (
              <svg viewBox="0 0 12 12" className="size-2.5 text-white" aria-hidden>
                <path
                  d="M2.5 6.2 4.8 8.5 9.5 3.8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
          </span>
          <span
            className={cx(
              'h-2 rounded-full',
              chosen ? 'w-full bg-ink-300' : 'w-2/3 bg-ink-200'
            )}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * One page, with a widget on it.
 *
 * The card inside the page used to be drawn here — a row of gold dots for
 * stars and two grey bars for text. It was fine and it was a second, worse
 * drawing of a review card, twenty lines below a section that renders the real
 * one. `WidgetThumbnail` is the product's own sketch of a layout, the same one
 * the dashboard's widget chooser and this site's navigation menu use, so the
 * three places that abbreviate a widget now abbreviate it identically.
 *
 * The grid, because that is what the section immediately below this draws.
 */
function DisplayCue() {
  return (
    <div
      className="w-44 rounded-2xl bg-white p-3 shadow-[0_12px_28px_-12px_rgba(10,10,11,0.25)] ring-1 ring-black/[0.05]"
      aria-hidden
    >
      <div className="flex items-center gap-1.5">
        <div className="h-1.5 w-10 rounded-full bg-ink-200" />
        {/* The page's own button. A website has one, and it is the detail that
            makes this read as a page rather than as a review card. */}
        <div className="ml-auto h-3 w-6 rounded-full bg-brand" />
      </div>
      <div className="mt-2.5">
        <WidgetThumbnail type="grid" />
      </div>
    </div>
  );
}
